import {ActionIcon as MantineActionIcon, Popover} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {
  IconThumbDown,
  IconThumbDownFilled,
  IconThumbUp,
  IconThumbUpFilled,
} from '@tabler/icons-react';
import type {FC} from 'react';
import {useState} from 'react';
import type {
  AssistantFeedbackReasonEnum,
  AssistantMessageFeedbackResponse,
} from 'src/api/ProactApiClient';
import {AssistantFeedbackRatingEnum} from 'src/api/ProactApiClient';
import {
  FEEDBACK_COMMENT_MAX_LENGTH,
  FEEDBACK_REASONS,
} from 'src/app/assistant/Feedback/messageFeedback';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Button} from 'src/design/atoms/Button';
import {SelectBox} from 'src/design/atoms/SelectBox';
import {Textbox} from 'src/design/atoms/Textbox';
import {
  useDeleteMessageFeedbackMutation,
  useSetMessageFeedbackMutation,
} from 'src/redux/queries/assistant';
import {useAlert} from 'src/shared/hooks/useAlert';
import {formatCount} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface MessageFeedbackActionsProps {
  conversationId: number;
  messageId: number;
  feedback: AssistantMessageFeedbackResponse | null | undefined;
}

const REASON_OPTIONS = FEEDBACK_REASONS.map(option => ({
  id: option.value,
  label: option.label,
}));


export const MessageFeedbackActions: FC<MessageFeedbackActionsProps> = ({
  conversationId,
  messageId,
  feedback,
}) => {
  const {setAlert} = useAlert();
  const [setFeedback, {isLoading: isSetting}] = useSetMessageFeedbackMutation();
  const [deleteFeedback, {isLoading: isDeleting}] =
    useDeleteMessageFeedbackMutation();
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [reason, setReason] = useState<AssistantFeedbackReasonEnum | null>(
    null
  );
  const [comment, setComment] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);

  const isUp = feedback?.rating === AssistantFeedbackRatingEnum.Up;
  const isDown = feedback?.rating === AssistantFeedbackRatingEnum.Down;
  const pending = isSetting || isDeleting;
  const target = {conversationId, messageId};

  const run = async (mutation: Promise<unknown>) => {
    try {
      await mutation;
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const removeVote = () => run(deleteFeedback(target).unwrap());

  const handleUpClick = () => {
    if (isUp) {
      void removeVote();
      return;
    }
    void run(
      setFeedback({
        ...target,
        request: {rating: AssistantFeedbackRatingEnum.Up},
      }).unwrap()
    );
  };

  const handleDownClick = () => {
    if (isDown) {
      void removeVote();
      return;
    }
    setReason(null);
    setComment('');
    setReasonMissing(false);
    setPopoverOpened(!popoverOpened);
  };

  const handleSubmit = () => {
    if (reason === null) {
      setReasonMissing(true);
      return;
    }
    setPopoverOpened(false);
    void run(
      setFeedback({
        ...target,
        request: {
          rating: AssistantFeedbackRatingEnum.Down,
          reason,
          comment: comment.trim() || undefined,
        },
      }).unwrap()
    );
  };

  return (
    <Group gap={2} wrap="nowrap">
      <ActionIcon
        tooltipText="Good response"
        color="black"
        aria-pressed={isUp}
        disabled={pending}
        onClick={handleUpClick}>
        {isUp ? <IconThumbUpFilled size={16} /> : <IconThumbUp size={16} />}
      </ActionIcon>
      <Popover
        opened={popoverOpened}
        onChange={setPopoverOpened}
        position="bottom-start"
        withArrow
        shadow="md"
        width={300}
        trapFocus>
        <Popover.Target>
          <MantineActionIcon
            size="sm"
            variant="subtle"
            color="black"
            aria-label="Bad response"
            aria-pressed={isDown}
            disabled={pending}
            onClick={handleDownClick}>
            {isDown ? (
              <IconThumbDownFilled size={16} />
            ) : (
              <IconThumbDown size={16} />
            )}
          </MantineActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap={0}>
            <SelectBox
              label="What went wrong?"
              placeholder="Choose a reason"
              withAsterisk
              withinPortal={false}
              options={REASON_OPTIONS}
              value={reason === null ? undefined : String(reason)}
              error={reasonMissing}
              onSelectChangeCallback={value => {
                setReason(
                  value ? (Number(value) as AssistantFeedbackReasonEnum) : null
                );
                setReasonMissing(false);
              }}
            />
            <Textbox
              label="Comment"
              placeholder="Tell us what the answer should have included"
              multiline
              minRows={2}
              maxRows={6}
              maxLength={FEEDBACK_COMMENT_MAX_LENGTH}
              value={comment}
              helperText={`${formatCount(comment.length)} / ${formatCount(FEEDBACK_COMMENT_MAX_LENGTH)}`}
              onChangeCallback={event => setComment(event.currentTarget.value)}
              props={{autosize: true}}
            />
            <Group gap="sm">
              <Button size="xs" onClick={handleSubmit}>
                Submit
              </Button>
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={() => setPopoverOpened(false)}>
                Cancel
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
};
